import { FirebaseApp } from "firebase/app"
import { AuthProvider } from "@refinedev/core"
import {
    Auth,
    getAuth,
    RecaptchaParameters,
    RecaptchaVerifier,
    signOut,
    updateEmail,
    updatePassword,
    updateProfile,
    getRedirectResult,
    User as FirebaseUser,
}
    from "firebase/auth";
import { IAuthCallbacks, ILoginWithEmailLinkArgs, ILoginWithGoogleArgs, ILoginWithPasswordArgs, ILoginArgs, IRegisterArgs, IAuthUser } from "./interfaces";
import { AuthActionResponse, CheckResponse, OnErrorResponse } from "@refinedev/core/dist/interfaces";
import { logger } from "@utils/logger";

export class FirebaseAuth {
    auth: Auth;

    constructor(
        readonly authActions?: IAuthCallbacks,
        auth?: Auth,
        firebaseApp?: FirebaseApp,
    ) {
        this.auth = auth || getAuth(firebaseApp);
        this.auth.useDeviceLanguage();

        this.getAuthProvider = this.getAuthProvider.bind(this);
        this.handleLogIn = this.handleLogIn.bind(this);
        this.handleRegister = this.handleRegister.bind(this);
        this.handleLogOut = this.handleLogOut.bind(this);
        this.onUpdateUserData = this.onUpdateUserData.bind(this);
        this.getUserIdentity = this.getUserIdentity.bind(this);
        this.handleCheckAuth = this.handleCheckAuth.bind(this);
        this.createRecaptcha = this.createRecaptcha.bind(this);
        this.getPermissions = this.getPermissions.bind(this);
        this.onErrorHandler = this.onErrorHandler.bind(this);

        this.auth?.onAuthStateChanged(user => {
            this.authActions?.onAuthChange?.(user!, this.auth)
        })

        getRedirectResult(this.auth).then(result => {
            console.log(result, result?.user)
            if (result?.user) {
                // If there's a user, we successfully returned from a redirect
                this.authActions?.onLogin?.(result.user, this);
            }
        }).catch(error => {
            // Handle redirect errors (e.g. account-exists-with-different-credential)
            this.onErrorHandler(error);
        });
    }

    public async handleLogOut(): Promise<AuthActionResponse> {
        await signOut(this.auth);
        await this.authActions?.onLogout?.(this.auth, this)
        return {
            success: true,
        };
    }

    public async handleLogIn({ type, ...args }: ILoginArgs): Promise<AuthActionResponse> {
        switch (type) {
            case 'emailLink':
                const { handleLogInWithLink } = await import('./loginProviders/withLink')
                return handleLogInWithLink(this, args as ILoginWithEmailLinkArgs)
            case 'emailLinkComplete':
                const { completeLogInWithLink } = await import('./loginProviders/withLink')
                return await completeLogInWithLink(this)
            case 'google':
                const { handleLogInWithGoogle } = await import('./loginProviders/withGoogle')
                return handleLogInWithGoogle(this, args as ILoginWithGoogleArgs)
            case 'googlePopup':
                const { handleLogInWithGooglePopup } = await import('./loginProviders/withGoogle')
                return handleLogInWithGooglePopup(this, args as ILoginWithGoogleArgs)
            case 'emailPassword':
                const { handleLogInWithPassword } = await import('./loginProviders/withPassword')
                return handleLogInWithPassword(this, args as ILoginWithPasswordArgs)
            default:
                throw Error("select a auth type as parameter to login")
        }
    }

    public async handleRegister({ type, ...args }: IRegisterArgs): Promise<AuthActionResponse> {
        logger.debug(type)
        switch (type) {
            case 'emailLink':
                const { handleLogInWithLink } = await import('./loginProviders/withLink')
                return handleLogInWithLink(this, args as ILoginWithEmailLinkArgs)
            case 'emailLinkComplete':
                const { completeLogInWithLink } = await import('./loginProviders/withLink')
                return await completeLogInWithLink(this)
            case 'google':
                const { handleLogInWithGoogle } = await import('./loginProviders/withGoogle')
                return handleLogInWithGoogle(this, args as ILoginWithGoogleArgs)
            case 'emailPassword':
                const { handleRegister } = await import('./loginProviders/withPassword')
                return handleRegister(this, args as ILoginWithPasswordArgs)
            default:
                throw Error("select a auth type as parameter to login")
        }
    }

    public async onUpdateUserData(args: IRegisterArgs) {
        await this.auth?.authStateReady()

        try {
            if (this.auth?.currentUser) {
                const { displayName, email, password } = args;
                if (password) {
                    await updatePassword(this.auth.currentUser, password);
                }

                if (email && this.auth.currentUser.email !== email) {
                    await updateEmail(this.auth.currentUser, email);
                }

                if (displayName && this.auth.currentUser.displayName !== displayName) {
                    await updateProfile(this.auth.currentUser, { displayName: displayName });
                }
            }
        } catch (error) {
            return Promise.reject(error);
        }
    }

    private async getUserIdentity(): Promise<IAuthUser | null> {
        await this.auth?.authStateReady()
        const user = this.auth?.currentUser;
        const userInfo = await this.authActions?.onIdentity?.(user!, this);

        if (!user) {
            return null
        }

        return {
            ...user,
            ...userInfo,
            ...(user?.email && { email: user.email }),
            ...(user?.displayName && { name: user.displayName }),
        };
    }

    private getFirebaseUser(): Promise<FirebaseUser> {
        return new Promise<FirebaseUser>((resolve, reject) => {
            const unsubscribe = this.auth?.onAuthStateChanged(user => {
                unsubscribe();
                resolve(user as FirebaseUser | PromiseLike<FirebaseUser>);
            }, reject);
        });
    }

    private async handleCheckAuth(): Promise<CheckResponse> {
        // logger.debug('check auth')
        const user = await this.getFirebaseUser()

        if (user) {
            const res = await this.authActions?.onCheck?.(user, this);
            return res ?? {
                authenticated: true,
            }
        } else {
            return {
                authenticated: false,
                logout: true,
                error: {
                    message: "Check failed",
                    name: "unauthorized",
                },
            }
        }
    }

    public async getPermissions(): Promise<string[]> {
        await this.auth?.authStateReady()

        const user = this.auth?.currentUser;
        logger.debug("user", user)
        const userPermissions = await this.authActions?.onPermissions?.(user!, this);
        logger.debug("user Perms", userPermissions)

        if (!userPermissions) {
            return []
        }
        return userPermissions
    }

    public createRecaptcha(containerOrId: string | HTMLDivElement, parameters?: RecaptchaParameters) {
        return new RecaptchaVerifier(this.auth, containerOrId, parameters);
    }

    public async onErrorHandler(error: any): Promise<OnErrorResponse> {
        // logger.debug(error)
        const user = await this.getFirebaseUser()

        const res = await this.authActions?.onCheck?.(user, error, this);

        if (res) {
            return res
        }

        // other dataproviders errors
        if (error.status === 401 || error.status === 403) {
            return {
                logout: true,
                redirectTo: "/login",
                error,
            };
        }

        return {};
    }

    public getAuthProvider(): AuthProvider {
        return {
            login: this.handleLogIn,
            register: this.handleRegister,
            logout: this.handleLogOut,
            check: this.handleCheckAuth,
            getPermissions: this.getPermissions,
            getIdentity: this.getUserIdentity,
            onError: this.onErrorHandler
        };
    }
}
